import { useEffect, useState, useCallback } from "react";
import { Reveal, Eyebrow, PageHero, PrimaryLink, OutlineLink } from "@/components/shared";
import { Play, Footprints, X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "@/data/content";

const Lightbox = ({ index, onClose, onPrev, onNext }) => {
  const item = GALLERY[index];

  const onKey = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    },
    [onClose, onPrev, onNext]
  );

  useEffect(() => {
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onKey]);

  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-[rgba(16,15,13,0.94)] flex items-center justify-center p-4 sm:p-10"
      role="dialog"
      aria-modal="true"
      aria-label={item.alt}
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close gallery viewer"
        className="absolute top-4 right-4 w-12 h-12 rounded-full border border-[rgba(255,255,255,0.25)] text-white flex items-center justify-center hover:bg-saffron hover:border-saffron hover:text-charcoal transition-colors duration-200"
        data-testid="lightbox-close-button"
      >
        <X size={22} />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        aria-label="Previous photo"
        className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-[rgba(255,255,255,0.25)] text-white flex items-center justify-center hover:bg-saffron hover:border-saffron hover:text-charcoal transition-colors duration-200"
        data-testid="lightbox-prev-button"
      >
        <ChevronLeft size={22} />
      </button>
      <figure className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
        <img src={item.src} alt={item.alt} className="w-full max-h-[80vh] object-contain rounded-xl" data-testid="lightbox-image" />
        <figcaption className="mt-4 text-center font-sans text-sm text-[rgba(255,255,255,0.7)]">
          {item.alt} — {index + 1} / {GALLERY.length}
        </figcaption>
      </figure>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        aria-label="Next photo"
        className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-[rgba(255,255,255,0.25)] text-white flex items-center justify-center hover:bg-saffron hover:border-saffron hover:text-charcoal transition-colors duration-200"
        data-testid="lightbox-next-button"
      >
        <ChevronRight size={22} />
      </button>
    </div>
  );
};

const Media = ({ scrollTo }) => {
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (scrollTo) {
      const t = setTimeout(() => {
        document.getElementById(scrollTo)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
      return () => clearTimeout(t);
    }
  }, [scrollTo]);

  return (
    <>
      <PageHero
        testId="media-hero"
        eyebrow="Media & Gallery"
        title="Moments of Seva"
        intro="Photographs, teachings and stories from IBMCT’s life of service — from the meditation hall to the open road of the padayatra."
      />

      <section className="bg-ivory" id="photos" data-testid="media-photos-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
          <Reveal>
            <Eyebrow>Photos</Eyebrow>
            <h2 className="text-[32px] sm:text-4xl lg:text-5xl font-semibold text-charcoal">The Gallery</h2>
            <p className="mt-4 text-bodytext/75 max-w-2xl">Click any photograph to view it in full.</p>
          </Reveal>
          <div className="mt-10 columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:balance]">
            {GALLERY.map((g, i) => (
              <Reveal key={g.src} delay={i * 60} className="mb-5 break-inside-avoid">
                <button
                  type="button"
                  onClick={() => setLightbox(i)}
                  className="group block w-full text-left cursor-pointer"
                  aria-label={`View photo: ${g.alt}`}
                  data-testid={`gallery-image-${i}`}
                >
                  <div className="img-zoom !rounded-2xl">
                    <img src={g.src} alt={g.alt} className="w-full object-cover" loading="lazy" />
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-charcoal text-white" id="videos" data-testid="media-videos-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
          <Reveal>
            <Eyebrow dark>Videos &amp; Sermons</Eyebrow>
            <h2 className="text-[32px] sm:text-4xl lg:text-5xl font-semibold text-white">Teachings &amp; Recordings</h2>
            <p className="mt-4 text-[rgba(255,255,255,0.78)] max-w-2xl">
              Our video library of Dhamma talks, sermons and event recordings is currently being curated for publication. Recordings will
              appear here as they are released.
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {["Dhamma Talks", "Sermons & Chanting", "Event Recordings"].map((title, i) => (
              <Reveal key={title} delay={i * 80}>
                <div className="border border-[rgba(255,255,255,0.16)] rounded-2xl aspect-video flex flex-col items-center justify-center gap-3 text-center px-6 bg-[rgba(255,255,255,0.03)]" data-testid={`video-placeholder-${i}`}>
                  <Play size={30} strokeWidth={1.5} className="text-saffron-light" aria-hidden="true" />
                  <p className="font-serif text-xl text-white">{title}</p>
                  <p className="font-sans text-[13px] text-[rgba(255,255,255,0.55)]">Coming soon</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ivory" id="padayatra" data-testid="media-padayatra-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Footprints size={32} strokeWidth={1.5} className="text-saffron" aria-hidden="true" />
            <Eyebrow>The Padayatra</Eyebrow>
            <h2 className="text-[32px] sm:text-4xl lg:text-5xl font-semibold text-charcoal">30,000 Kilometres on Foot</h2>
            <p className="mt-6 text-bodytext/80 leading-relaxed">
              Beginning with a 90 KM walk in 2012, Venerable Bhante Bodhidatta Thera’s padayatra grew into one of the most remarkable walking
              pilgrimages of our time — over 30,000 kilometres completed by 2019, on foot, through villages, towns and cities across
              India.
            </p>
            <p className="mt-4 text-bodytext/80 leading-relaxed">
              Each step carried a message: peace over conflict, compassion over indifference, and the Buddha’s timeless call to care for
              all beings. Along the way, thousands joined, listened and walked beside him.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <PrimaryLink to="/leadership" testId="padayatra-leader-button">Read About Venerable Bhante Bodhidatta Thera</PrimaryLink>
              <OutlineLink to="/contact" testId="padayatra-contact-button">Invite a Talk</OutlineLink>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="img-zoom !rounded-2xl aspect-[4/3]">
              <img src="/assets/gallery/g1.webp" alt="Monks walking together in procession during the padayatra" className="w-full h-full object-cover" loading="lazy" data-testid="padayatra-image" />
            </div>
          </Reveal>
        </div>
      </section>

      {lightbox !== null && (
        <Lightbox
          index={lightbox}
          onClose={() => setLightbox(null)}
          onPrev={() => setLightbox((lightbox + GALLERY.length - 1) % GALLERY.length)}
          onNext={() => setLightbox((lightbox + 1) % GALLERY.length)}
        />
      )}
    </>
  );
};

export default Media;
